import type { Result } from "@pdfking/errors"
import type {
  CMYKColor,
  Distance,
  Margins as MarginsGeneric,
  Pixel,
  Point as PointGeneric,
  Rectangle as RectangleGeneric,
  RGBColor,
  SpatialRectangle as SpatialRectangleGeneric,
} from "@pdfking/math"
import type { Matrix, PageNumber } from "@pdfking/renderer-api"
import type { Bytes, EnumUnion, Integer } from "@pdfking/structs"

export type {
  CMYKColor,
  ColorComponent,
  Degree,
  Distance,
  Percentage,
  Pixel,
  RGBColor,
} from "@pdfking/math"
export type { Bytes } from "@pdfking/structs"
export type { PageNumber }

export type SpatialRectangle = SpatialRectangleGeneric<Pixel>
export type Rectangle = RectangleGeneric<Pixel>
export type Margins = MarginsGeneric<Pixel>
export type DistancePoint = PointGeneric<Distance>
// an anchor and its Bézier handles, a missing one already on its anchor (see
// the renderer-api's PathPoint)
export type DistancePathPoint = DistancePoint & {
  before: DistancePoint
  after: DistancePoint
}
export type DistanceRectangle = SpatialRectangleGeneric<Distance>
export type DistanceMargins = MarginsGeneric<Distance>

// a spot ink of its own, plotted as a Separation named `name`: its CMYK only
// what shows wherever the ink is not
export type SpotColor = CMYKColor & { spot_name: string }
export type PlotColor = EnumUnion<{ rgb: RGBColor; cmyk: CMYKColor; spot: SpotColor }>
export type HorizontalAlign = "center" | "left" | "right"
export type VerticalAlign = "bottom" | "center" | "top"

// Branded integers (usize = pointer-sized, adapts to 32/64-bit)
export type PdfReaderPointer = Integer<"PdfReaderPointer", "usize">
export type ICCPointer = Integer<"ICCPointer", "usize">
export type GetBlockPointer = Integer<"GetBlockPointer", "usize">
export type IsDataAvailPointer = Integer<"IsDataAvailPointer", "usize">

// Pdfium-specific data types
// A page as whoever places it sees it (see the renderer-api's Media): its
// TrimBox, and how far its MediaBox reaches past it on each edge, as the page
// is drawn (turned by /Rotate). Where the boxes sit stays with pdfium.
export type MediaInfo = {
  trim: Rectangle
  bleed: Margins
}

// A page's bytes and what it draws with, split out of a file or plotted: its
// pdf — or, plotted with a `raster`, its RGBA pixels — and `media`, its extent
// (in points for a pdf, in raster pixels for pixels)
export type DrawnPageResult = {
  bytes: Uint8Array
  media: MediaInfo
  // what the page draws with: rgb/cmyk/gray/lab and spot colorants by name,
  // each once, sorted — a plot's, the colors it sets and its media's
  colors: string[]
  // whether anything it draws is transparent (soft masks, opacity, blend
  // modes; a plot's color below full alpha, or a media placed that is):
  // without, gs -dNOTRANSPARENCY draws it the same
  transparency: boolean
}

// The merge output's layout (see renderer-pdfium's StreamFormat): merge
// writes it, renderer-pdfium reads it back. Every section starts with the
// header, its marker, its length and its meta's
export const MERGE_HEADER = "%PDF-1.5\r\n"
export const MERGE_SECTION_MARKER = "%PDF-Meta "
export const MERGE_SHARED_MARKER = "%PDF-Base "
export const MERGE_SECTION_DIGITS = 16
export const MERGE_META_DIGITS = 8
// how far from its end a section's trailer starts, at most
export const MERGE_TRAILER_LENGTH = 96

// A page handed to merge: a one-page pdf, and the meta its section records
// for split to read without opening it — one line, base64 (see StreamFormat)
export type MergePage = {
  bytes: Uint8Array
  meta: string
}

// The page as the plot API authors it, every default already filled in:
// measured in `Distance`, y-up from the trim's bottom-left, colors by id, every
// placement a matrix. Rust converts to pixels, resolves the colors and walks
// the group tree; this is passed through untouched.
export type PlotMatrix = Matrix<Distance>

export type PlotShape = EnumUnion<{
  plot_group: {
    matrix: PlotMatrix
    shapes: PlotShape[]
  }
  plot_rect: {
    // the OCG named after it the shape is drawn in, "" for none
    layer: string
    position: DistanceRectangle
    fill_color: string
    stroke_color: string
    stroke_width: Distance
  }
  plot_media: {
    layer: string
    position: DistanceRectangle
    mask: DistanceMargins
    filename: string
    page_number: PageNumber
    media_matrix: PlotMatrix
  }
  plot_path: {
    layer: string
    points: DistancePathPoint[]
    closed: boolean
    fill_color: string
    stroke_color: string
    stroke_width: Distance
  }
  plot_text: {
    layer: string
    position: DistanceRectangle
    text: string
    font_name: string
    font_size: Pixel
    horizontal_align: HorizontalAlign
    vertical_align: VerticalAlign
    fill_color: string
    stroke_color: string
    stroke_width: Distance
    text_color: string
  }
}>

export type PlotShapeGroup = Extract<PlotShape, { enum: "plot_group" }>
export type PlotShapeRect = Extract<PlotShape, { enum: "plot_rect" }>
export type PlotShapeMedia = Extract<PlotShape, { enum: "plot_media" }>
export type PlotShapePath = Extract<PlotShape, { enum: "plot_path" }>
export type PlotShapeText = Extract<PlotShape, { enum: "plot_text" }>

export type PlotPage = {
  width: Distance
  height: Distance
  bleed: DistanceMargins
  shapes: PlotShape[]
}

/** A `file_map` page: the single-page PDF and the file's own matrix, written
 * about the centre of whichever box shows it. */
export type PlotFile = {
  bytes: Uint8Array
  matrix: PlotMatrix
}

/** A `layer_map` layer: how its OCG is shown, printed and edited. A layer
 * the map has not is visible, printed and unlocked. */
export type PlotLayer = {
  visible: boolean
  printable: boolean
  locked: boolean
}

// A plot drawn straight to pixels instead of written as a pdf: at
// `resolution`, over the trim grown by `bleed` (in points) whatever the page's
// own bleed, soft-proofed through `icc_handle` (0 for none)
export type PlotRaster = {
  resolution: Pixel
  bleed: Margins
  icc_handle: ICCPointer
}

export type PlotPageEntry = {
  page: PlotPage
  unit_to_px: Pixel
  color_map: Record<string, PlotColor>
  /** by filename, then page number */
  file_map: Record<string, Record<PageNumber, PlotFile>>
  layer_map: Record<string, PlotLayer>
  raster?: PlotRaster
}

// Function signatures
export type init = () => void
export type destroy = () => void
export type open = (
  file_len: Bytes,
  get_block_ptr: GetBlockPointer,
  is_data_avail_ptr: IsDataAvailPointer
) => Result<PdfReaderPointer>
export type doc_avail = (reader_handle: PdfReaderPointer) => Result<boolean>
export type page_avail = (
  reader_handle: PdfReaderPointer,
  page_index: PageNumber
) => Result<boolean>
export type page_count = (reader_handle: PdfReaderPointer) => bigint
export type open_custom = (
  file_len: Bytes,
  get_block_ptr: GetBlockPointer
) => Result<PdfReaderPointer>
export type open_mem = (data: Uint8Array) => Result<PdfReaderPointer>
export type close_mem = (reader_handle: PdfReaderPointer) => Result<boolean>
export type close = (reader_handle: PdfReaderPointer) => Result<boolean>
// The merge output (see renderer-pdfium's StreamFormat), written as the pages
// come: one page pulled from `pages` per step, its section yielded — the
// shared section with the first — and the tail once `pages` ends. Offsets
// start at `start_offset`, the room left for the integrity line.
export type merge = (
  pages: Generator<MergePage>,
  start_offset: bigint
) => Generator<Result<Uint8Array>>
// What follows a section cut out on its own (its last bytes `trailer`) and
// the shared section's objects appended at `at`, `length` long: their xref,
// as an update of the section, so the page is a whole pdf again. `shared` is
// the shared section's meta line.
export type shared_update = (
  trailer: Uint8Array,
  shared: Uint8Array,
  at: bigint,
  length: bigint
) => Result<Uint8Array>
export type split_page = (
  reader_handle: PdfReaderPointer,
  page_index: PageNumber
) => Result<DrawnPageResult>
export type convert_jpeg = (data: Uint8Array, resolution: Pixel) => Result<DrawnPageResult>
export type convert_bitmap = (
  bgra: Uint8Array,
  size: Rectangle,
  resolution: Pixel
) => Result<DrawnPageResult>
export type icc_create = (data: Uint8Array) => Result<ICCPointer>
export type icc_destroy = (handle: ICCPointer) => Result<boolean>
export type plot = (entry: PlotPageEntry) => Result<DrawnPageResult>
